import { Injectable } from '@angular/core';
import {
  map,
  merge,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  LanguageSettingsOption,
  UserLanguageSettings,
  UserLanguageSettingsAdapter,
} from 'shared/core';
import { EmployeeSettings } from '../../model/employee/employee-settings';
import { normalizePreferredLanguage } from '../../model/employee/employee-settings-language';
import { UpdateEmployeeSettings } from '../../model/employee/request/update-employee-settings-request';
import { ProfileDataService } from './profile-data.service';
import { preferredLanguages } from 'shared/transcription';

@Injectable({
  providedIn: 'root',
})
export class HelperLanguageSettingsAdapterService implements UserLanguageSettingsAdapter {
  private readonly updatesSubject = new Subject<UserLanguageSettings>();

  readonly updates$ = this.updatesSubject.asObservable();
  readonly preferredLanguageOptions: LanguageSettingsOption[] =
    preferredLanguages.map(({ locale, name, flag }) => ({
      code: locale,
      label: `${flag} ${name}`,
    }));
  readonly settings$: Observable<UserLanguageSettings | null> = merge(
    this.profileDataService
      .loadProfileData()
      .pipe(map(({ settings }) => settings)),
    this.profileDataService.employeeSettings$,
  ).pipe(
    map((settings) => this.mapToLanguageSettings(settings)),
    shareReplay(1),
  );

  constructor(private readonly profileDataService: ProfileDataService) {}

  updateLanguageSettings(
    settings: UserLanguageSettings,
  ): Observable<UserLanguageSettings> {
    return this.profileDataService.employeeSettings$.pipe(
      take(1),
      switchMap((currentSettings) => {
        const request = {
          displayLanguage: currentSettings.displayLanguage,
          preferredLanguage: normalizePreferredLanguage(
            settings.preferredLanguage ?? currentSettings.preferredLanguage,
          ),
          photoCountdownSeconds: currentSettings.photoCountdownSeconds,
          liveImageDurationSeconds: currentSettings.liveImageDurationSeconds,
          favoriteSmsCountryCodes: currentSettings.favoriteSmsCountryCodes,
        } as UpdateEmployeeSettings;

        return this.profileDataService.updateProfileSettings(request).pipe(
          map((updatedSettings) => this.mapToLanguageSettings(updatedSettings)),
          tap((updatedSettings) => {
            if (updatedSettings) {
              this.updatesSubject.next(updatedSettings);
            }
          }),
        );
      }),
    );
  }

  private mapToLanguageSettings(
    settings: EmployeeSettings,
  ): UserLanguageSettings;
  private mapToLanguageSettings(
    settings: EmployeeSettings | null,
  ): UserLanguageSettings | null;
  private mapToLanguageSettings(
    settings: EmployeeSettings | null,
  ): UserLanguageSettings | null {
    if (!settings) {
      return null;
    }

    return {
      preferredLanguage: normalizePreferredLanguage(settings.preferredLanguage),
    };
  }
}
